import React, { useEffect, useState } from 'react';
import formatFileURL from './formatUrl';
import { AWS_File_URL, UPLOAD_ROOT_PATH } from '../../utils/Constants';
import styled from 'styled-components';
import { BodySmall, ErrorMessage } from './GlobalElements/Typography';
import { tokens } from "../../public/tokens.js";

const { colors, typography, globals } = tokens;

const SignatureContainer = styled.div`
  max-width: 28rem;
  width: 100%;
  position: relative;
  padding: 2rem 1rem;
  border: 1px solid ${colors.surfaces['border-divider']};
  border-radius: ${globals['border-radius']};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 1rem;
`;

const Spinner = styled.div`
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border: 3px solid ${tokens.colors.theme['button-color']};
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UploadedImage = styled.img`
  color: ${tokens.colors.theme['highlight-color']};
  max-height: 175px;
  max-width: 100%;
  margin: 0 auto;
  display: block;
  object-fit: contain;
`;

const RemoveButton = styled.div`
  position: absolute;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  color: ${tokens.colors.theme['button-color']};
  top: 6px;
  right: 10px;
  
  &:hover {
    color: ${props => props.disabled ? tokens.colors.theme['button-color'] : tokens.colors.theme['button-hover-color']};
  }
  
  i {
    margin-left: 0.5rem;
  }
`;

const UploadArea = styled.label`
  text-align: center;
  background-color: ${props => props.dragActive ? tokens.colors.theme['hover-color'] : `transparent`};
  display: block;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const UploadIcon = styled.i`
  font-size: 48px;
  color: ${colors.text.link};
`;

const BrowseLink = styled.label`
  font-weight: ${typography.fontWeights.regular};
  color: ${tokens.colors.theme['button-color']};
  cursor: pointer;
`;

const UploadText = styled(BodySmall)`
  margin-top: 0.75rem;
  margin-bottom: 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadSignatureFile = ({
  isAssessmentReview, 
  fileInputRef, 
  formik, 
  setShowIp, 
  readOnly = false, 
  readOnlyData, 
  setIsSig, 
  isCancel, 
  from, 
  removeSignature
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [image, setImage] = useState(null);
  const [readImage, setReadImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [localImage, setLocalImage] = useState(null);

  useEffect(() => {
    if (isCancel) {
      setImage(null);
      setShowIp(null);
    }
  }, [isCancel]);

  useEffect(() => {
    if (image && from == 'quiz') {
      setLocalImage(image);
    }
  }, [image, from]);

  useEffect(() => {
    if (readOnly && readOnlyData) {
      setImageLoading(true);
      setImageError(false);
      
      var readImage;
      if (
        "fileUploadedFrom" in readOnlyData &&
        readOnlyData?.fileUploadedFrom === "1" &&
        !readOnlyData?.signature?.startsWith("https://")
      ) {
        readImage =
          from === "quiz"
            ? AWS_File_URL + readOnlyData?.signature
            : readOnlyData?.student_signature;
      } else {
        const filePath =
          from === "quiz"
            ? readOnlyData?.signature
            : readOnlyData?.student_signature;

        readImage = formatFileURL(filePath);
      }
      
      if (readImage && !readImage.endsWith('undefined')) {
        const img = new Image();
        
        img.onload = () => {
          setImageLoading(false);
          setReadImage(readImage);
          if (from == 'quiz') {
            setLocalImage(null);
          }
          localStorage.removeItem('localImage');
        };
        
        img.onerror = () => {
          setImageLoading(false);
          setImageError(true);
        };
        img.src = readImage;
      }
    }
  }, [readOnly, readOnlyData]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
      formik && formik.setFieldValue('student_signature', e.dataTransfer.files[0]);
      setIsSig(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
      formik && formik.setFieldValue('student_signature', e.target.files[0]);
      setIsSig(e.target.files[0]);
    }
  };

  const handleFiles = (files) => {
    setShowIp(true);
    const file = files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const showRemoveButton = !isAssessmentReview && readOnly && from == 'quiz' && readOnlyData?.signature_type == 1;
  const shouldShowRemoveButton = showRemoveButton || (!readOnly && (image || readImage));

  return (
    <SignatureContainer>
      {(image || readOnly) ? (
        <>
          {(imageLoading || localImage) && !imageError && (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          )}
          
          {imageError && (
            <ErrorMessage>
              Failed to load image. Please try again.
            </ErrorMessage>
          )}
          
          {!imageLoading && !imageError && !localImage && (
            <>
              <UploadedImage 
                src={readOnly ? readImage : from == 'quiz' ? localImage : image} 
                alt="Uploaded" 
              />
              
              {shouldShowRemoveButton && (
                <RemoveButton 
                  disabled={readOnly && from !== 'quiz'}
                  onClick={() => {
                    if (from == 'quiz') {
                      setImage("");
                      removeSignature(1);
                      setShowIp(false);
                      setReadImage("");
                    } else {
                      setImage(null);
                      setShowIp(false);
                      formik && formik.setFieldValue('student_signature', null);
                      setIsSig(null);
                    }
                  }}
                >
                  Remove Uploaded Image<i className='fal fa-trash-alt'></i>
                </RemoveButton>
              )}
            </>
          )}
        </>
      ) : (
        <UploadArea 
          htmlFor="file-upload"
          dragActive={dragActive}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <UploadIcon className='fal fa-upload' />
          <UploadText>
            To upload your signature, Drop your file here, or{' '}
            <BrowseLink htmlFor="file-upload">
              click here to browse
            </BrowseLink>
          </UploadText>
          <BrowseLink>
            Supported Files: JPG and PNG
          </BrowseLink>
          <HiddenInput
            id="file-upload"
            name="file-upload"
            type="file"
            ref={fileInputRef}
            onChange={handleChange}
            accept=".jpg,.png"
          />
        </UploadArea>
      )}
    </SignatureContainer>
  );
};

export default UploadSignatureFile;